/**
 * E-PREFETCH-1 — Routen-Chunks laden, bevor der Klick kommt.
 *
 * `App.tsx` haelt jede Seite als `lazy(() => import(...))`. Der Download des
 * Routen-Chunks startet damit erst in dem Moment, in dem die Route gemountet
 * wird, und der Bediener sieht bis dahin den Suspense-Fallback. Der Zeiger
 * steht aber meist schon Hunderte Millisekunden vorher auf dem
 * Navigationseintrag. Diese Zeit nutzen wir.
 *
 * Ein Prefetch ist reine Kuer: schlaegt er fehl (Netz weg, Chunk nach einem
 * Deploy nicht mehr da), schweigt er. Der echte Mount laeuft danach durch den
 * normalen Suspense-Pfad und zeigt dort seinen eigenen Fehler.
 *
 * E-BUNDLE-124 — die Zuordnungstabelle selbst liegt seit dieser Aenderung in
 * `routePrefetchTable.ts` und wird erst beim ersten `prefetchRoute` geholt.
 * Diese Datei haengt statisch im Shell-Layout (`AppSidebar`,
 * `MobileBottomNav`), also im Einstiegs-Chunk; die Tabelle mit ihren 29
 * `import()`-Ausdruecken kostete dort gemessene 4061 Bytes fuer etwas, das der
 * erste Bildaufbau nie anfasst. Der erste Zeigerkontakt laedt jetzt zuerst die
 * Tabelle und danach den Routen-Chunk. Beides passiert im Hintergrund, lange
 * bevor der Klick kommt, und der spaetere Mount wartet in keinem Fall darauf.
 */

/**
 * Jeder Pfad wird hoechstens einmal angestossen. Der Browser-Cache wuerde
 * einen zweiten Aufruf zwar ohnehin abfangen, aber ein Zeiger, der ueber eine
 * Liste wandert, loest sonst je Eintrag mehrfach aus.
 *
 * Anders als vor E-BUNDLE-124 landet auch ein Pfad OHNE Zuordnung hier: ob
 * eine Zuordnung besteht, weiss diese Datei erst nach dem Laden der Tabelle,
 * also nach dem synchronen Teil des Aufrufs. Ein unbekannter Pfad hat ohnehin
 * nichts vorzuladen — ihn zu vermerken spart nur weitere Fehlgriffe.
 */
const started = new Set<string>();

/** Die Tabelle wird hoechstens einmal geholt; der Fehlschlag wird nicht gemerkt. */
let tablePromise: Promise<Readonly<Record<string, () => Promise<unknown>>>> | null = null;

function routeChunks(): Promise<Readonly<Record<string, () => Promise<unknown>>>> {
  tablePromise ??= import("./routePrefetchTable.js").then((mod) => mod.ROUTE_CHUNKS);
  return tablePromise;
}

/**
 * Laedt den Chunk der Zielroute im Hintergrund.
 *
 * Aufrufer sind `onMouseEnter` und `onFocus` der Navigationseintraege, also
 * Ereignisse, die noch keine Absicht beweisen. Entsprechend darf die Funktion
 * nichts kosten, was sichtbar waere: kein Zustand, kein Rendern, kein Fehler.
 */
export function prefetchRoute(to: string): void {
  const path = (to.split("?")[0] ?? "").split("#")[0] ?? "";
  if (!path || started.has(path)) {
    return;
  }
  started.add(path);
  void routeChunks()
    .then((chunks) => chunks[path]?.())
    .catch(() => {
      // Ein fehlgeschlagener Prefetch ist folgenlos: der Pfad bleibt als
      // "versucht" vermerkt, und der echte Mount meldet seinen Fehler selbst.
      // Die Tabelle selbst wird freigegeben, damit ein Netzaussetzer beim
      // allerersten Zeigerkontakt nicht jeden spaeteren Prefetch mitnimmt.
      tablePromise = null;
    });
}

/** Nur fuer Tests — setzt das Gedaechtnis der bereits angestossenen Pfade zurueck. */
export function resetRoutePrefetchForTests(): void {
  started.clear();
  tablePromise = null;
}
